import React from 'react'
import styled from 'styled-components'
import { Radio } from '@base-ui-components/react/radio'
import { RadioGroup } from '@base-ui-components/react/radio-group'

export type RadioGroupSize = 'small' | 'medium' | 'large'

export interface RadioGroupFieldProps {
    items: Record<string | number, string>
    value: string | number | undefined
    onChange: (value: string) => void
    size?: RadioGroupSize
    columns?: number
    maxItemsPerColumn?: number
    ariaLabel?: string
}

function RadioDotIcon(props: React.ComponentProps<'svg'>) {
    const { ref, ...safeProps } = props
    return (
        <Icon fill="currentcolor" viewBox="0 0 10 10" {...safeProps}>
            <circle cx="5" cy="5" r="3" />
        </Icon>
    )
}

export function RadioGroupField(props: RadioGroupFieldProps) {
    const {
        items,
        value,
        onChange,
        size = 'medium',
        columns = 1,
        maxItemsPerColumn,
        ariaLabel,
    } = props

    const entries = Object.entries(items)

    const optionColumns = splitOptionsIntoColumns(
        entries,
        columns,
        maxItemsPerColumn,
    )

    return (
        <Container>
            <Group
                data-size={size}
                value={value === undefined ? '' : String(value)}
                onValueChange={(v) => typeof v === 'string' && onChange(v)}
                aria-label={ariaLabel}
            >
                {columns === 1 ? (
                    entries.map(([key, label]) => (
                        <Item key={key} data-size={size}>
                            <Root value={String(key)} data-size={size}>
                                <Indicator>
                                    <RadioDotIcon />
                                </Indicator>
                            </Root>
                            <ItemText data-size={size}>{label}</ItemText>
                        </Item>
                    ))
                ) : (
                    <ColumnsContainer columns={columns}>
                        {optionColumns.map((columnOptions, columnIndex) => (
                            <Column key={columnIndex}>
                                {columnOptions.map(([key, label]) => (
                                    <Item key={key} data-size={size}>
                                        <Root
                                            value={String(key)}
                                            data-size={size}
                                        >
                                            <Indicator>
                                                <RadioDotIcon />
                                            </Indicator>
                                        </Root>
                                        <ItemText data-size={size}>
                                            {label}
                                        </ItemText>
                                    </Item>
                                ))}
                            </Column>
                        ))}
                    </ColumnsContainer>
                )}
            </Group>
        </Container>
    )
}

function splitOptionsIntoColumns<T>(
    options: T[],
    columns: number,
    maxItemsPerColumn?: number,
): T[][] {
    if (columns === 1) return [options]

    const columnsArray: T[][] = Array.from({ length: columns }, () => [])

    if (maxItemsPerColumn) {
        let currentColumn = 0
        for (const option of options) {
            if (columnsArray[currentColumn].length >= maxItemsPerColumn) {
                currentColumn = (currentColumn + 1) % columns
            }
            columnsArray[currentColumn].push(option)
        }
    } else {
        const itemsPerColumn = Math.ceil(options.length / columns)
        options.forEach((option, index) => {
            const columnIndex = Math.floor(index / itemsPerColumn)
            if (columnIndex < columns) {
                columnsArray[columnIndex].push(option)
            }
        })
    }

    return columnsArray
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
    width: 100%;
    box-sizing: border-box;
`

const ColumnsContainer = styled.div<{ columns: number }>`
    display: grid;
    grid-template-columns: repeat(${({ columns }) => columns}, 1fr);
    gap: ${({ theme }) => theme.spacing[8]};
    width: 100%;
`

const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
`

const Group = styled(RadioGroup)`
    display: flex;
    flex-direction: column;
    align-items: start;
    color: ${({ theme }) => theme.colors.text.primary};
    border: 1px solid ${({ theme }) => theme.colors.greyScale[200]};
    border-radius: ${({ theme }) => theme.borderRadius['2xl']};
    padding: ${({ theme }) => theme.spacing[8]};
    box-sizing: border-box;
    gap: ${({ theme }) => theme.spacing[2]};
    width: 100%;
`

const Item = styled.label`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[4]};
    cursor: pointer;
    &:hover {
        background-color: ${({ theme }) => theme.colors.greyScale[400]};
    }
    border-radius: ${({ theme }) => theme.borderRadius['lg']};
    // prettier-ignore
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) =>
        theme.spacing[2]};
    width: 100%;

    &[data-size='small'] {
        gap: ${({ theme }) => theme.spacing[2]};
        // prettier-ignore
        padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) =>
            theme.spacing[2]};
    }

    &[data-size='large'] {
        gap: ${({ theme }) => theme.spacing[5]};
        // prettier-ignore
        padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) =>
            theme.spacing[3]};
    }
`

const ItemText = styled.span`
    flex: 1;
    font-size: ${({ theme }) => theme.typography.fontSize.lg};

    &[data-size='small'] {
        font-size: ${({ theme }) => theme.typography.fontSize.lg};
    }

    &[data-size='large'] {
        font-size: ${({ theme }) => theme.typography.fontSize.xl};
    }
`

const Root = styled(Radio.Root)`
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    outline: 0;
    padding: 0;
    margin: 0;
    border: none;

    width: 1.25rem;
    height: 1.25rem;

    &[data-size='small'] {
        width: 1rem;
        height: 1rem;
    }

    &[data-size='large'] {
        width: 1.5rem;
        height: 1.5rem;
    }

    &[data-unchecked] {
        border: 1px solid ${({ theme }) => theme.colors.border.primary};
        background-color: transparent;
    }

    &[data-checked] {
        background-color: ${({ theme }) => theme.colors.text.primary};
    }

    &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.info};
        outline-offset: 2px;
    }
`

const Indicator = styled(Radio.Indicator)`
    display: flex;
    color: ${({ theme }) => theme.colors.text.inverse};

    &[data-unchecked] {
        display: none;
    }
`

const Icon = styled.svg`
    width: 0.5rem;
    height: 0.5rem;

    [data-size='small'] & {
        width: 0.4rem;
        height: 0.4rem;
    }

    [data-size='large'] & {
        width: 0.6rem;
        height: 0.6rem;
    }
`

export default RadioGroupField
